import { reactive } from 'vue'
import store from '@/store'
import {
	setUser,
	getUser,
	getMyUser,
	getOrganization,
	isMyUser,
} from './memberships'
import { getGroupChats } from './chats'
import { normalizeMediaItem } from '@/helpers/utils'
import router from '@/router'
import debounce from 'debounce'
import moment from 'moment'
import { chatCan, orgCan, CREATE_ANNOUNCEMENT } from '@/helpers/permissions'

const announcementsById = reactive({})

export const announcementsData = {
	announcementsById,
}

const sortIds = (id1, id2) => {
	return id2 - id1
}

const isOld = (announcement) => {
	if (!announcement) return null
	const previous = getAnnouncement(announcement.id)
	if (previous && announcement.updated_at && previous.updated_at) {
		return moment(previous.updated_at).isAfter(announcement.updated_at)
	}
	return false
}

const normalize = (announcement) => {
	if (!announcement) return null
	const { sender, ...rest } = announcement
	setUser(sender)

	return {
		...rest,
		isSentByMe: isMyUser(rest.senderuser_id),
		...normalizeState(rest),
		...normalizeMedia(rest),
		...normalizeTranslation(rest),
	}
}

const normalizeState = (announcement) => {
	const isRead = !!announcement.read
	const isSent = !!announcement.sent
	const isUnread = !isRead && isSent
	const isScheduled = !!announcement.scheduled && !isSent
	return { isRead, isSent, isUnread, isScheduled }
}

const normalizeMedia = ({ announcementmedia = [] }) => {
	const hasMedia = announcementmedia.length > 0
	const normalized = announcementmedia.map(normalizeMediaItem)
	return {
		hasMedia,
		media: normalized[0],
		announcementmedia: normalized,
	}
}

const normalizeTranslation = (announcement) => {
	let {
		trans,
		text,
		canTranslate,
		announcementtrans,
		isTranslated = false,
	} = announcement
	if (!trans && announcementtrans?.length > 0) {
		// if there are multiple translations, we'll need to find the one
		// in the list that matches our preferred language
		const me = getMyUser()
		trans = announcementtrans.find((at) => at.lang === me.language)
		// determine if user can later translate
		const myLangs = [store.language, ...(me.languages || [])]
		const iUnderstandLang = myLangs.includes(announcement.lang)
		canTranslate = announcement.lang !== store.language && !iUnderstandLang
		// determine if it should be auto translated
		isTranslated = !!(canTranslate && me.autotranslate)
	}
	text = isTranslated ? trans.translation : announcement.announcement
	return { trans, text, canTranslate, isTranslated }
}

const emitAlert = () => {
	EventBus.$emit('announcement-update')
	checkShowAnnouncementsBanner()
}

export const setAnnouncement = (announcement, skipAlert) => {
	if (!announcement) return null
	if (isOld(announcement)) {
		announcement = getAnnouncement(announcement.id)
	} else {
		announcement = normalize(announcement)
		announcementsById[announcement.id] = announcement
	}
	if (!skipAlert) emitAlert()
}

export const setAnnouncements = (announcements) => {
	if (!announcements) return null
	const normalized = announcements
		.map((a) => setAnnouncement(a, true))
		.filter((a) => a)
	emitAlert()
	return normalized
}

export const updateAnnouncement = (id, dataOrUpdater = {}) => {
	if (!id) return null
	const announcement = getAnnouncement(id) || { id }
	if (typeof dataOrUpdater === 'function') {
		return setAnnouncement(dataOrUpdater(announcement))
	}
	return setAnnouncement({ ...announcement, ...dataOrUpdater })
}

export const deleteAnnouncement = (id) => {
	delete announcementsById[id]
	emitAlert()
}

const addSender = (a) => {
	if (!a) return a
	a.sender = getUser(a.senderuser_id)
	return a
}

export const getAnnouncement = (announcementId) => {
	if (!announcementId) return null
	return announcementsById[announcementId] || null
}

export const getAnnouncementWithSender = (announcementId) => {
	if (!announcementId) return null
	return addSender(getAnnouncement(announcementId))
}

export const getAllAnnouncements = () => {
	return Object.keys(announcementsById).sort(sortIds).map(getAnnouncement)
}

export const getScheduledAnnouncements = () => {
	return getAllAnnouncements().filter((a) => a?.isScheduled)
}

export const getScheduledAnnouncementsWithSenders = () => {
	return getScheduledAnnouncements().map(addSender)
}

export const getSentAnnouncements = () => {
	return getAllAnnouncements().filter((a) => a?.isSent)
}

export const getSentAnnouncementsWithSenders = () => {
	return getSentAnnouncements().map(addSender)
}

export const getUnreadAnnouncements = () => {
	return getAllAnnouncements().filter((a) => a?.isUnread)
}

export const getUnreadAnnouncementsWithSenders = () => {
	return getUnreadAnnouncements().map(addSender)
}

export const getUnreadAnnouncementsCount = () => {
	return getUnreadAnnouncements().length || 0
}

export const isAnnouncementsEnabled = () => {
	const org = getOrganization()
	return org?.announcements_enabled || false
}

export const canSendGroupAnnouncements = () => {
	if (!isAnnouncementsEnabled()) return false
	return getGroupChats().some((g) => chatCan(g, CREATE_ANNOUNCEMENT))
}

export const canSendOrgAnnouncements = () => {
	if (!isAnnouncementsEnabled()) return false
	return orgCan(CREATE_ANNOUNCEMENT)
}

export const canSendAnnouncements = () => {
	return canSendOrgAnnouncements() || canSendGroupAnnouncements()
}

const routesToShowBanner = [
	'/chat',
	'/recents',
	'/pinned',
	'/settings',
	'/tasks',
	'/search',
]
const checkShowAnnouncementsBanner = debounce(() => {
	// this is an attempt to manage the store.showAnnouncementsBanner flag in one place
	const { announcements_enabled } = getOrganization() || {}
	const { path = '' } = router.currentRoute?.value || router.currentRoute || {}
	const isMatchingRoute = routesToShowBanner.some((r) => path.startsWith(r))
	const count = getUnreadAnnouncementsCount()
	const show = announcements_enabled && count > 0 && isMatchingRoute
	store.showAnnouncementsBanner = show
}, 25)

setTimeout(() => {
	router.afterEach(checkShowAnnouncementsBanner)
}, 25)
